import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subject, take, takeUntil } from 'rxjs';
import { MeasurementDataService } from '../../services/measurement-data.service';
import { MeasurementData, MarkerAnnotation } from '../../models/measurement-view.types';
import { MarkerAnnotationModalComponent } from '../marker-annotation-modal/marker-annotation-modal.component';
import { MarkerAnnotationModalData } from '../marker-annotation-modal/marker-annotation-modal.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

export type ControlSettings = {
    selectionMode: 'select' | 'hover';
    amplitudeSize: 'disabled' | 'small' | 'medium' | 'large';
    timeFormat: 'absolute' | 'relative';
};

@Component({
    selector: 'app-control-panel',
    templateUrl: './control-panel.component.html',
    styleUrls: ['./control-panel.component.scss'],
    standalone: false
})
export class ControlPanelComponent implements OnInit, OnDestroy {
    @Output() public settingsChanged = new EventEmitter<ControlSettings>();

    private destroy$ = new Subject<void>();

    public currentMeasurement: MeasurementData | null = null;

    // Control settings
    public selectionMode: 'select' | 'hover' = 'hover';
    public amplitudeSize: 'disabled' | 'small' | 'medium' | 'large' = 'disabled';
    public timeFormat: 'absolute' | 'relative' = 'absolute';

    public constructor(
        private measurementDataService: MeasurementDataService,
        private modalService: NgbModal
    ) {}

    public ngOnInit(): void {
        this.measurementDataService.currentMeasurement$.pipe(takeUntil(this.destroy$)).subscribe((measurement) => {
            this.currentMeasurement = measurement;
        });

        // Load settings from sessionStorage if available
        this.loadSettings();

        // Emit loaded settings to parent component
        this.emitSettings();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public openMarkerAnnotation() {
        const modalRef = this.modalService.open(MarkerAnnotationModalComponent, {
            size: 'lg'
        });
        const markers = this.currentMeasurement?.dynamicData?.marker_annotations || [];
        const modalData: MarkerAnnotationModalData = {
            measurementId: this.currentMeasurement?.dynamicData?.measurementId,
            annotation: this.currentMeasurement?.dynamicData?.annotation,
            markers:
                [...markers]
                    ?.map((m) => ({ number: m.marker_num, time: m.time, annotation: m.annotation }))
                    ?.sort((a, b) => a.number - b.number) || []
        };
        (modalRef.componentInstance as MarkerAnnotationModalComponent).data = modalData;

        modalRef.closed.pipe(take(1)).subscribe((hasChanges: boolean) => {
            if (hasChanges) {
                const modalData = (modalRef.componentInstance as MarkerAnnotationModalComponent).data;
                this.currentMeasurement.dynamicData.annotation = modalData.annotation;

                // Map MarkerAnnotationItem back to MarkerAnnotation and update via service
                const markerAnnotations: MarkerAnnotation[] = modalData.markers.map((m) => ({
                    marker_num: m.number,
                    time: m.time,
                    annotation: m.annotation
                }));
                this.measurementDataService.updateMarkerAnnotations(markerAnnotations);
            }
        });
    }

    public onSelectionModeChange(): void {
        this.saveSettings();
        this.emitSettings();
    }

    public onAmplitudeSizeChange(): void {
        this.saveSettings();
        this.emitSettings();
    }

    public onTimeFormatChange(): void {
        this.saveSettings();
        this.emitSettings();
    }

    private loadSettings(): void {
        const savedSettings = sessionStorage.getItem('medicalMonitorSettings');
        if (savedSettings) {
            try {
                const settings = JSON.parse(savedSettings) as ControlSettings;
                this.selectionMode = settings.selectionMode || 'hover';
                this.amplitudeSize = settings.amplitudeSize || 'disabled';
                this.timeFormat = settings.timeFormat || 'absolute';
            } catch (e) {
                console.warn('Failed to load settings from sessionStorage', e);
            }
        }
    }

    private saveSettings(): void {
        const settings: ControlSettings = {
            selectionMode: this.selectionMode,
            amplitudeSize: this.amplitudeSize,
            timeFormat: this.timeFormat
        };

        try {
            sessionStorage.setItem('medicalMonitorSettings', JSON.stringify(settings));
        } catch (e) {
            console.warn('Failed to save settings to sessionStorage', e);
        }
    }

    private emitSettings(): void {
        const settings: ControlSettings = {
            selectionMode: this.selectionMode,
            amplitudeSize: this.amplitudeSize,
            timeFormat: this.timeFormat
        };

        this.settingsChanged.emit(settings);
    }
}
